import { useMsal } from '@azure/msal-react';
import { Fragment, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogTitle, Typography, DialogContent, Stack, Button } from '@mui/material';
import { useIdleTimer } from 'react-idle-timer';
import { useTimer } from '../../hooks/use-timer';
import { getEnv } from '../../helper/get-env';
import { useFindAppConfig } from '../ConfigurationContext/ConfigurationContext';
import { useGlobalContext } from '../GlobalContextProvider';
import { localAuth } from '../../api/auth';

export type TimeoutProviderProps = PropsWithChildren;

const TimeoutProvider = ({ children }: TimeoutProviderProps) => {
    const [timerConfig, setTimerConfig] = useState({});

    const logoutTimeoutValue = useFindAppConfig('Logout timeout');
    const globalContext = useGlobalContext();

    const { start, seconds } = useTimer();

    const [open, setOpen] = useState(false);
    const { instance, accounts } = useMsal();

    const logoutHandler = async () => {
        if (open) {
            setOpen(false);
        }

        if (accounts.length > 0) {
            const account = instance.getActiveAccount();
            localAuth.logout();

            await instance.logoutRedirect({
                account,
                postLogoutRedirectUri: await getEnv('REDIRECT_URI_AFTER_LOGOUT', true)
            });
        }
    };

    const notLogoutHandler = () => {
        idleTimer.reset();
    };

    const logoutHandlerWithoutUserInput = useCallback(async () => {
        if (accounts.length > 0) {
            const account = instance.getActiveAccount();
            localAuth.logout();

            await instance.logoutRedirect({
                account,
                postLogoutRedirectUri: '/logoutMessage'
            });
        }
    }, [accounts.length, instance])

    const idleTimer = useIdleTimer(timerConfig);

    useEffect(() => {
        if (seconds <= 0) {
            setOpen(false);
        }
    }, [seconds]);

    useEffect(() => {
        const setIdleTimerConfig = async () => {
            const sessionTimeout = await getEnv('LOGIN_SESSION_TIMEOUT', true);
            const promptTimeout = await getEnv('LOGIN_PROMPT_TIMEOUT', true);

            return {
                debounce: 0,
                timeout: 1000 * 60 * parseInt(logoutTimeoutValue?.value || sessionTimeout, 10),
                promptBeforeIdle: 1000 * parseInt(promptTimeout, 10),
                crossTab: true,
                onIdle: async () => {
                    globalContext.handleCaseSave && (await globalContext.handleCaseSave(false));
                    logoutHandlerWithoutUserInput();
                },
                onPrompt: () => {
                    if (instance.getActiveAccount()) {
                        start(parseInt(promptTimeout, 10));
                        setOpen(true);
                    }
                }
            };
        };

        setIdleTimerConfig().then((config) => {
            setTimerConfig(config);
        });
    }, [globalContext, instance, logoutHandlerWithoutUserInput, logoutTimeoutValue?.value, start]);

    return (
        <Fragment>
            <Dialog open={open}>
                <DialogTitle fontSize={20} fontWeight={'bold'}>
                    Are you still here?
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <Typography>
                            You will be logged out from all of the TurningPoint services!
                        </Typography>
                        <Typography>Your login session will be expired in {seconds}</Typography>
                        <Stack direction={'row'} gap={2}>
                            <Button onClick={logoutHandler} variant="contained" fullWidth>
                                Logout
                            </Button>
                            <Button
                                onClick={() => {
                                    setOpen(false);
                                    notLogoutHandler();
                                }}
                                variant="outlined"
                                fullWidth>
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
            {children}
        </Fragment>
    );
};
export default TimeoutProvider;
